import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteRsvpsByFilterApplicationErrors,
  BulkDeleteRsvpsByFilterOptions,
  BulkDeleteRsvpsByFilterResponse,
  BulkUpdateRsvpsApplicationErrors,
  BulkUpdateRsvpsOptions,
  BulkUpdateRsvpsResponse,
  CancelRsvpGuestsCheckInApplicationErrors,
  CancelRsvpGuestsCheckInOptions,
  CancelRsvpGuestsCheckInResponse,
  CheckInRsvpGuestsApplicationErrors,
  CheckInRsvpGuestsOptions,
  CheckInRsvpGuestsResponse,
  CountRsvpsOptions,
  CountRsvpsResponse,
  CreateRsvpApplicationErrors,
  CreateRsvpOptions,
  GetRsvpOptions,
  ListCurrentRsvpsApplicationErrors,
  ListCurrentRsvpsOptions,
  ListCurrentRsvpsResponse,
  ListRsvpSummaryResponse,
  MaskedRsvp,
  QueryRsvpsOptions,
  QueryRsvpsResponse,
  Rsvp,
  RsvpCreatedEnvelope,
  RsvpDeletedEnvelope,
  RsvpQuery,
  RsvpSearch,
  RsvpUpdatedEnvelope,
  RsvpsQueryBuilder,
  SearchRsvpsOptions,
  SearchRsvpsResponse,
  UpdateRsvp,
  UpdateRsvpApplicationErrors,
  UpdateRsvpOptions,
  bulkDeleteRsvpsByFilter as universalBulkDeleteRsvpsByFilter,
  bulkUpdateRsvps as universalBulkUpdateRsvps,
  cancelRsvpGuestsCheckIn as universalCancelRsvpGuestsCheckIn,
  checkInRsvpGuests as universalCheckInRsvpGuests,
  countRsvps as universalCountRsvps,
  createRsvp as universalCreateRsvp,
  deleteRsvp as universalDeleteRsvp,
  getRsvp as universalGetRsvp,
  listCurrentRsvps as universalListCurrentRsvps,
  listRsvpSummary as universalListRsvpSummary,
  queryRsvps as universalQueryRsvps,
  searchRsvps as universalSearchRsvps,
  typedQueryRsvps as universalTypedQueryRsvps,
  updateRsvp as universalUpdateRsvp,
} from './events-v2-rsvp-rsvp-v-2.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function searchRsvps(httpClient: HttpClient): SearchRsvpsSignature {
  return (search: RsvpSearch, options?: SearchRsvpsOptions) =>
    universalSearchRsvps(
      search,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchRsvpsSignature {
  /**
   * Retrieves a list of RSVPs that match the specified search query and optionally performs aggregations on the data queried.
   *
   * To learn about working with _search_, see [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language), [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/rest/articles/get-started/field-projection).
   * @param - Search options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details.
   */
  (search: RsvpSearch, options?: SearchRsvpsOptions): Promise<
    NonNullablePaths<
      SearchRsvpsResponse,
      | `rsvps`
      | `rsvps.${number}.status`
      | `rsvps.${number}.onlineConferencingLogin.link`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function queryRsvps(httpClient: HttpClient): QueryRsvpsSignature {
  return (options?: QueryRsvpsOptions) =>
    universalQueryRsvps(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryRsvpsSignature {
  /**
   * Creates a query to retrieve a list of RSVPs.
   *
   * The `queryRsvps()` method builds a query to retrieve a list of RSVPs and returns an `RsvpsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `RsvpsQueryBuilder` methods onto the query. `RsvpsQueryBuilder` methods enable you to sort, filter, and control the results `queryRsvps()` returns.
   *
   * `queryRsvps()` runs with these `RsvpsQueryBuilder` defaults, which you can override:
   *
   * - limit(50)
   * - descending("_createdDate")
   *
   * The methods that are chained to `queryRsvps()` are applied in the order they're called. For example, if you apply ascending('status') and then descending('eventId'), the results are sorted first by the status, and then, if there are multiple results with the same status, the items are sorted by event ID.
   *
   * **Note:** This method is quite slow. We suggest you using the following filter pairs for the most optimal speed:
   * - `eventId` and `status`
   * - `eventId` and `fullyCheckedIn`
   * - `eventId` and `memberId`
   * - `eventId` and `contactId`
   * - `eventId` and `createdDate`
   */
  (options?: QueryRsvpsOptions): RsvpsQueryBuilder;
}

export function typedQueryRsvps(
  httpClient: HttpClient
): TypedQueryRsvpsSignature {
  return (query: RsvpQuery, options?: QueryRsvpsOptions) =>
    universalTypedQueryRsvps(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryRsvpsSignature {
  /** */
  (query: RsvpQuery, options?: QueryRsvpsOptions): Promise<
    NonNullablePaths<
      QueryRsvpsResponse,
      | `rsvps`
      | `rsvps.${number}.status`
      | `rsvps.${number}.onlineConferencingLogin.link`,
      5
    >
  >;
}

export function countRsvps(httpClient: HttpClient): CountRsvpsSignature {
  return (options?: CountRsvpsOptions) =>
    universalCountRsvps(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountRsvpsSignature {
  /**
   * Counts RSVPs with the specified filtering and searching.
   */
  (options?: CountRsvpsOptions): Promise<
    NonNullablePaths<CountRsvpsResponse, `count`, 2>
  >;
}

export function getRsvp(httpClient: HttpClient): GetRsvpSignature {
  return (rsvpId: string, options?: GetRsvpOptions) =>
    universalGetRsvp(
      rsvpId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRsvpSignature {
  /**
   * Retrieves an RSVP by ID.
   * @param - ID of the RSVP to retrieve.
   * @returns Retrieved RSVP.
   */
  (rsvpId: string, options?: GetRsvpOptions): Promise<
    NonNullablePaths<
      Rsvp,
      | `form.inputValues`
      | `form.inputValues.${number}.inputName`
      | `form.inputValues.${number}.value`
      | `status`
      | `guestList.guests`
      | `additionalGuestDetails.guestNames`
      | `onlineConferencingLogin.link`,
      5
    >
  >;
}

export function createRsvp(httpClient: HttpClient): CreateRsvpSignature {
  return (
    rsvp: NonNullablePaths<
      Rsvp,
      | `additionalGuestDetails.guestCount`
      | `email`
      | `eventId`
      | `firstName`
      | `lastName`
      | `status`,
      3
    >,
    options?: CreateRsvpOptions
  ) =>
    universalCreateRsvp(
      rsvp,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateRsvpSignature {
  /**
   * Creates an RSVP.
   *
   * To create anRSVP with multiple guests, you have to specify the `additionalGuests` and `guestNames` fields.
   * @param - RSVP info.
   * @returns Created RSVP.
   */
  (
    rsvp: NonNullablePaths<
      Rsvp,
      | `additionalGuestDetails.guestCount`
      | `email`
      | `eventId`
      | `firstName`
      | `lastName`
      | `status`,
      3
    >,
    options?: CreateRsvpOptions
  ): Promise<
    NonNullablePaths<
      Rsvp,
      | `form.inputValues`
      | `form.inputValues.${number}.inputName`
      | `form.inputValues.${number}.value`
      | `status`
      | `guestList.guests`
      | `additionalGuestDetails.guestNames`
      | `onlineConferencingLogin.link`,
      5
    > & {
      __applicationErrorsType?: CreateRsvpApplicationErrors;
    }
  >;
}

export function updateRsvp(httpClient: HttpClient): UpdateRsvpSignature {
  return (_id: string, rsvp: UpdateRsvp, options?: UpdateRsvpOptions) =>
    universalUpdateRsvp(
      _id,
      rsvp,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateRsvpSignature {
  /**
   * Updates RSVP.
   *
   * Each time the RSVP is updated, `revision` increments by 1. The existing `revision` must be specified when updating the RSVP. This ensures you're working with the latest RSVP and prevents unintended overwrites.
   * @param - ID of the RSVP.
   * @returns Updated RSVP.
   */
  (_id: string, rsvp: UpdateRsvp, options?: UpdateRsvpOptions): Promise<
    NonNullablePaths<
      Rsvp,
      | `form.inputValues`
      | `form.inputValues.${number}.inputName`
      | `form.inputValues.${number}.value`
      | `status`
      | `guestList.guests`
      | `additionalGuestDetails.guestNames`
      | `onlineConferencingLogin.link`,
      5
    > & {
      __applicationErrorsType?: UpdateRsvpApplicationErrors;
    }
  >;
}

export function deleteRsvp(httpClient: HttpClient): DeleteRsvpSignature {
  return (rsvpId: string) =>
    universalDeleteRsvp(
      rsvpId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteRsvpSignature {
  /**
   * Deletes an RSVP.
   * @param - ID of the RSVP to delete.
   */
  (rsvpId: string): Promise<void>;
}

export function bulkUpdateRsvps(
  httpClient: HttpClient
): BulkUpdateRsvpsSignature {
  return (
    rsvps: NonNullablePaths<MaskedRsvp, `rsvp._id` | `rsvp.revision`, 3>[],
    options?: BulkUpdateRsvpsOptions
  ) =>
    universalBulkUpdateRsvps(
      rsvps,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateRsvpsSignature {
  /**
   * Updates multiple RSVPs.
   *
   * You can update up to 100 RSVPs per request.
   * @param - RSVPs to update.
   */
  (
    rsvps: NonNullablePaths<MaskedRsvp, `rsvp._id` | `rsvp.revision`, 3>[],
    options?: BulkUpdateRsvpsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateRsvpsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.rsvp.status`
      | `results.${number}.rsvp.onlineConferencingLogin.link`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateRsvpsApplicationErrors;
    }
  >;
}

export function bulkDeleteRsvpsByFilter(
  httpClient: HttpClient
): BulkDeleteRsvpsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkDeleteRsvpsByFilterOptions
  ) =>
    universalBulkDeleteRsvpsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteRsvpsByFilterSignature {
  /**
   * Deletes multiple RSVPs.
   * @param - Filter object.
   */
  (
    filter: Record<string, any>,
    options?: BulkDeleteRsvpsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkDeleteRsvpsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkDeleteRsvpsByFilterApplicationErrors;
    }
  >;
}

export function checkInRsvpGuests(
  httpClient: HttpClient
): CheckInRsvpGuestsSignature {
  return (rsvpId: string, options?: CheckInRsvpGuestsOptions) =>
    universalCheckInRsvpGuests(
      rsvpId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CheckInRsvpGuestsSignature {
  /**
   * Checks in RSVP guests.
   *
   * You can check in up to 11 guests per request.
   * @param - ID of the RSVP to check-in.
   */
  (rsvpId: string, options?: CheckInRsvpGuestsOptions): Promise<
    NonNullablePaths<
      CheckInRsvpGuestsResponse,
      | `rsvp.form.inputValues`
      | `rsvp.form.inputValues.${number}.inputName`
      | `rsvp.form.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.guestList.guests`
      | `rsvp.additionalGuestDetails.guestNames`
      | `rsvp.onlineConferencingLogin.link`,
      6
    > & {
      __applicationErrorsType?: CheckInRsvpGuestsApplicationErrors;
    }
  >;
}

export function cancelRsvpGuestsCheckIn(
  httpClient: HttpClient
): CancelRsvpGuestsCheckInSignature {
  return (rsvpId: string, options?: CancelRsvpGuestsCheckInOptions) =>
    universalCancelRsvpGuestsCheckIn(
      rsvpId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelRsvpGuestsCheckInSignature {
  /**
   * Cancels the check-in of RSVP guests.
   *
   * If the request body is empty, cancels all check-ins from the RSVP. After the cancellation, the `guests.checkInDate` value becomes empty.
   *
   * You can cancel the check-in of up to 11 guests per request.
   * @param - ID of the RSVP to cancel check-in for.
   */
  (rsvpId: string, options?: CancelRsvpGuestsCheckInOptions): Promise<
    NonNullablePaths<
      CancelRsvpGuestsCheckInResponse,
      | `rsvp.form.inputValues`
      | `rsvp.form.inputValues.${number}.inputName`
      | `rsvp.form.inputValues.${number}.value`
      | `rsvp.status`
      | `rsvp.guestList.guests`
      | `rsvp.additionalGuestDetails.guestNames`
      | `rsvp.onlineConferencingLogin.link`,
      6
    > & {
      __applicationErrorsType?: CancelRsvpGuestsCheckInApplicationErrors;
    }
  >;
}

export function listRsvpSummary(
  httpClient: HttpClient
): ListRsvpSummarySignature {
  return (eventId: string[]) =>
    universalListRsvpSummary(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface ListRsvpSummarySignature {
  /**
   * Retrieves an RSVP summary.
   * @param - Event ID to retrieve the RSVP summary for.
   */
  (eventId: string[]): Promise<
    NonNullablePaths<ListRsvpSummaryResponse, `summaries`, 2>
  >;
}

export function listCurrentRsvps(
  httpClient: HttpClient
): ListCurrentRsvpsSignature {
  return (eventId: string[], options?: ListCurrentRsvpsOptions) =>
    universalListCurrentRsvps(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCurrentRsvpsSignature {
  /**
   * Lists RSVPs for the authenticated member across specified events.
   * @param - Event IDs to retrieve member RSVPs for.
   */
  (eventId: string[], options?: ListCurrentRsvpsOptions): Promise<
    NonNullablePaths<
      ListCurrentRsvpsResponse,
      | `rsvps`
      | `rsvps.${number}.status`
      | `rsvps.${number}.onlineConferencingLogin.link`,
      5
    > & {
      __applicationErrorsType?: ListCurrentRsvpsApplicationErrors;
    }
  >;
}

export const onRsvpCreated = EventDefinition(
  'wix.events.v2.rsvp_created',
  true,
  (event: RsvpCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.form.inputValues.dateTime' },
            { path: 'entity.guestList.guests.checkInDetails.checkInDate' },
            {
              path: 'entity.guestList.guests.checkInDetails.checkInCanceledDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.form.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.form.inputValues.address.address' }],
        },
      ])
    )
)<RsvpCreatedEnvelope>();
export const onRsvpDeleted = EventDefinition(
  'wix.events.v2.rsvp_deleted',
  true,
  (event: RsvpDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.form.inputValues.dateTime' },
            { path: 'entity.guestList.guests.checkInDetails.checkInDate' },
            {
              path: 'entity.guestList.guests.checkInDetails.checkInCanceledDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.form.inputValues.number' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.form.inputValues.address.address' }],
        },
      ])
    )
)<RsvpDeletedEnvelope>();
export const onRsvpUpdated = EventDefinition(
  'wix.events.v2.rsvp_updated',
  true,
  (event: RsvpUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.form.inputValues.dateTime' },
            { path: 'entity.guestList.guests.checkInDetails.checkInDate' },
            {
              path: 'entity.guestList.guests.checkInDetails.checkInCanceledDate',
            },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.form.inputValues.dateTime' },
            {
              path: 'modifiedFields.guestList.guests.checkInDetails.checkInDate',
            },
            {
              path: 'modifiedFields.guestList.guests.checkInDetails.checkInCanceledDate',
            },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.form.inputValues.number' },
            { path: 'modifiedFields.form.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.form.inputValues.address.address' },
            { path: 'modifiedFields.form.inputValues.address.address' },
          ],
        },
      ])
    )
)<RsvpUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdditionalGuest,
  AdditionalGuestDetails,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  Agenda,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  Alignment,
  AnchorData,
  App,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  AspectRatio,
  AudioData,
  Background,
  BackgroundBackgroundOneOf,
  BackgroundImage,
  BackgroundType,
  Badge,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkActionMetadata,
  BulkDeleteRsvpsByFilterOptions,
  BulkDeleteRsvpsByFilterRequest,
  BulkDeleteRsvpsByFilterResponse,
  BulkRsvpResult,
  BulkUpdateRsvpsOptions,
  BulkUpdateRsvpsRequest,
  BulkUpdateRsvpsResponse,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CalendarLinks,
  CancelRsvpGuestsCheckInOptions,
  CancelRsvpGuestsCheckInRequest,
  CancelRsvpGuestsCheckInResponse,
  CaptionData,
  CardStyles,
  CardStylesType,
  Category,
  CategoryCounts,
  CellStyle,
  CheckInDetails,
  CheckInRsvpGuestsOptions,
  CheckInRsvpGuestsRequest,
  CheckInRsvpGuestsResponse,
  CheckoutFormMessages,
  CheckoutType,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  CommonAddress,
  CommonAddressLocation,
  CommonAddressStreetOneOf,
  CommonStreetAddress,
  CommonSubdivision,
  ConferenceType,
  CountRsvpsOptions,
  CountRsvpsRequest,
  CountRsvpsResponse,
  CreateRsvpOptions,
  CreateRsvpRequest,
  CreateRsvpResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomTag,
  Dashboard,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteRsvpRequest,
  DeleteRsvpResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventCreated,
  EventData,
  EventDeleted,
  EventDisplaySettings,
  EventMetadata,
  EventStatus,
  EventType,
  ExtendedFields,
  ExternalEvent,
  Feed,
  File,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  Form,
  FormMessages,
  FormResponse,
  FormSubmission,
  FormattedAddress,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetRsvpOptions,
  GetRsvpRequest,
  GetRsvpResponse,
  Gradient,
  GroupByValueResults,
  Guest,
  GuestLimitExceededData,
  GuestList,
  GuestListConfig,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImageStyles,
  IncludeMissingValuesOptions,
  InitialExpandedItems,
  Input,
  InputControl,
  InputControlType,
  InputValue,
  Interval,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Keyword,
  Label,
  LabellingSettings,
  LargeCursorPaging,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutType,
  LimitedRsvpSpotsReleased,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListCurrentRsvpsOptions,
  ListCurrentRsvpsRequest,
  ListCurrentRsvpsResponse,
  ListRsvpSummaryRequest,
  ListRsvpSummaryResponse,
  ListRsvpsRequest,
  ListRsvpsResponse,
  ListValue,
  Location,
  LocationType,
  MapCoordinates,
  MapData,
  MapSettings,
  MapType,
  MaskedRsvp,
  Media,
  MentionData,
  MessageEnvelope,
  Metadata,
  MissingValues,
  Mode,
  ModificationOptions,
  Money,
  Negative,
  NegativeResponseConfirmation,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Occurrence,
  Oembed,
  OnlineConferencing,
  OnlineConferencingConfig,
  OnlineConferencingLogin,
  OnlineConferencingSession,
  Option,
  OptionDesign,
  OptionLayout,
  OptionSelection,
  OptionSelectionSelectedOptionOneOf,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Page,
  Pages,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  Positive,
  PositiveResponseConfirmation,
  PricingData,
  QueryRsvpsOptions,
  QueryRsvpsRequest,
  QueryRsvpsResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  Recurrences,
  Registration,
  RegistrationClosedMessages,
  RegistrationStatus,
  Rel,
  RequestedFields,
  Resizing,
  ResponseConfirmation,
  ResponsivenessBehaviour,
  RestoreInfo,
  Results,
  RibbonStyles,
  RichContent,
  Rsvp,
  RsvpBalanceDelta,
  RsvpCollection,
  RsvpCollectionConfig,
  RsvpConfirmationMessages,
  RsvpConfirmationMessagesNegativeResponseConfirmation,
  RsvpConfirmationMessagesPositiveResponseConfirmation,
  RsvpCreatedEnvelope,
  RsvpDeletedEnvelope,
  RsvpFormMessages,
  RsvpQuerySpec,
  RsvpSearchSpec,
  RsvpStatus,
  RsvpStatusOptions,
  RsvpSummary,
  RsvpUpdatedEnvelope,
  RsvpsBalanceUpdated,
  RsvpsQueryBuilder,
  RsvpsQueryResult,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  Scaling,
  ScheduleConfig,
  Scheduling,
  Search,
  SearchDetails,
  SearchPagingMethodOneOf,
  SearchRsvpsOptions,
  SearchRsvpsRequest,
  SearchRsvpsResponse,
  SearchRsvpsWithOffsetRequest,
  SearchRsvpsWithOffsetResponse,
  SeoSchema,
  SeoSettings,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SiteUrl,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  StandardDetails,
  State,
  Status,
  StreetAddress,
  Styles,
  StylesBorder,
  StylesPosition,
  Subdivision,
  SubdivisionType,
  TableCellData,
  TableData,
  Tag,
  Target,
  TaxConfig,
  TaxType,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  Ticketing,
  TicketingConfig,
  TicketingSummary,
  TicketsConfirmationMessages,
  TicketsUnavailableMessages,
  Type,
  URI,
  URIs,
  UpdateRsvp,
  UpdateRsvpOptions,
  UpdateRsvpRequest,
  UpdateRsvpResponse,
  V2RsvpSummary,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  ValueType,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VisitorType,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  utils,
} from './events-v2-rsvp-rsvp-v-2.universal.js';
